/**
 * LocalizedTimeInput - Time input respecting user's 12h/24h preference
 * Displays time in user's preferred format, outputs HH:mm (24h) for storage
 * Clock icon opens an analog clock face picker with configurable step
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { useUserPreferences } from '../context';
import { ClockFacePicker } from './ClockFacePicker';

export interface LocalizedTimeInputProps {
  /** Time value in HH:mm (24h) format */
  value: string | null;
  /** Called with HH:mm (24h) format or null */
  onChange: (value: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  id?: string;
  /** Minute step for time picker (default: 5) */
  step?: number;
}

/** Parse 24h input like "14:30" */
function parse24h(input: string): { hours: number; minutes: number } | null {
  const match = input.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

/** Parse 12h input like "2:30 PM" or "2:30PM" */
function parse12h(input: string): { hours: number; minutes: number } | null {
  const match = input.trim().match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (!match) return null;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toLowerCase();
  if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;
  if (period === 'am' && hours === 12) hours = 0;
  else if (period === 'pm' && hours !== 12) hours += 12;
  return { hours, minutes };
}

/**
 * Flexible time parser — tries multiple common formats on blur.
 * Supports: 0600, 06:00, 06.00, 600, 6:00pm, 06:00pm, 0600pm, 6.00pm, 6pm, etc.
 */
export function parseFlexible(input: string): { hours: number; minutes: number } | null {
  const s = input.trim();
  if (!s) return null;

  // Try strict parsers first
  const strict24 = parse24h(s);
  if (strict24) return strict24;
  const strict12 = parse12h(s);
  if (strict12) return strict12;

  // Extract AM/PM suffix if present
  const ampmMatch = s.match(/^(.+?)\s*(am|pm|a\.m\.|p\.m\.)$/i);
  const body = ampmMatch ? ampmMatch[1].trim() : s;
  const period = ampmMatch ? (ampmMatch[2].toLowerCase().startsWith('p') ? 'pm' : 'am') : null;

  let hours: number | null = null;
  let minutes: number | null = null;

  // "HH.MM" or "H.MM" (dot separator)
  const dotMatch = body.match(/^(\d{1,2})\.(\d{2})$/);
  if (dotMatch) {
    hours = parseInt(dotMatch[1], 10);
    minutes = parseInt(dotMatch[2], 10);
  }

  // "HHMM" (4 digits, no separator) e.g. 0600, 1800
  if (hours === null) {
    const fourDigit = body.match(/^(\d{4})$/);
    if (fourDigit) {
      hours = parseInt(body.slice(0, 2), 10);
      minutes = parseInt(body.slice(2, 4), 10);
    }
  }

  // "HMM" (3 digits) e.g. 600 → 6:00
  if (hours === null) {
    const threeDigit = body.match(/^(\d{3})$/);
    if (threeDigit) {
      hours = parseInt(body.slice(0, 1), 10);
      minutes = parseInt(body.slice(1, 3), 10);
    }
  }

  // "H" or "HH" (hour only, no minutes) e.g. 6pm, 18
  if (hours === null) {
    const hourOnly = body.match(/^(\d{1,2})$/);
    if (hourOnly) {
      hours = parseInt(hourOnly[1], 10);
      minutes = 0;
    }
  }

  if (hours === null || minutes === null) return null;

  // Apply AM/PM conversion
  if (period) {
    if (hours < 1 || hours > 12) return null;
    if (period === 'am' && hours === 12) hours = 0;
    else if (period === 'pm' && hours !== 12) hours += 12;
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

/** Convert HH:mm to 12h display string */
function to12hDisplay(hhmm: string): string {
  const parsed = parse24h(hhmm);
  if (!parsed) return '';
  const { hours, minutes } = parsed;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

/** Convert HH:mm to 24h display string */
function to24hDisplay(hhmm: string): string {
  const parsed = parse24h(hhmm);
  if (!parsed) return '';
  return `${String(parsed.hours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')}`;
}

/** Format to HH:mm for storage */
function toStorageFormat(hours: number, minutes: number): string {
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function LocalizedTimeInput({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  id,
  step = 5,
}: LocalizedTimeInputProps) {
  const { t } = useTranslation('common');
  const { formatOptions } = useUserPreferences();
  const is12h = formatOptions.timeFormat === '12h';

  const generatedId = React.useId();
  const inputId = id || `time-input-${generatedId}`;

  const formatForDisplay = useCallback(
    (hhmm: string | null): string => {
      if (!hhmm) return '';
      return is12h ? to12hDisplay(hhmm) : to24hDisplay(hhmm);
    },
    [is12h],
  );

  const [displayValue, setDisplayValue] = useState(() => formatForDisplay(value));
  const [hasError, setHasError] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // pickerHour stores the display hour: 0-23 in 24h mode, 1-12 in 12h mode
  const initParsed = value ? parse24h(value) : null;
  const [pickerHour, setPickerHour] = useState<number | null>(() => {
    if (!initParsed) return null;
    if (is12h) return initParsed.hours === 0 ? 12 : initParsed.hours > 12 ? initParsed.hours - 12 : initParsed.hours;
    return initParsed.hours;
  });
  const [pickerMinute, setPickerMinute] = useState<number | null>(initParsed?.minutes ?? null);
  const [pickerPeriod, setPickerPeriod] = useState<'AM' | 'PM'>(
    initParsed ? (initParsed.hours >= 12 ? 'PM' : 'AM') : 'AM',
  );

  // Sync picker state when value changes externally
  useEffect(() => {
    const p = value ? parse24h(value) : null;
    if (p) {
      setPickerHour(is12h ? (p.hours === 0 ? 12 : p.hours > 12 ? p.hours - 12 : p.hours) : p.hours);
      setPickerMinute(p.minutes);
      setPickerPeriod(p.hours >= 12 ? 'PM' : 'AM');
    }
  }, [value, is12h]);

  // Sync display when value or format changes externally
  useEffect(() => {
    const formatted = formatForDisplay(value);
    const currentParsed = parseDisplay(displayValue);
    const currentStorage = currentParsed ? toStorageFormat(currentParsed.hours, currentParsed.minutes) : null;
    if (value !== currentStorage) {
      setDisplayValue(formatted);
      setHasError(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, formatForDisplay]);

  // Default to 9:00 AM when picker opens with no value
  const handlePickerOpenChange = useCallback(
    (open: boolean) => {
      if (open && !value) {
        setPickerHour(9);
        setPickerMinute(0);
        setPickerPeriod('AM');
      }
      setPickerOpen(open);
    },
    [value],
  );

  const parseDisplay = useCallback(
    (input: string): { hours: number; minutes: number } | null => {
      if (!input.trim()) return null;
      return is12h ? parse12h(input) : parse24h(input);
    },
    [is12h],
  );

  const inputPattern = useMemo(() => {
    return is12h ? /^[\d:. aAmMpP]*$/ : /^[\d:.aAmMpP]*$/;
  }, [is12h]);

  const maxLength = is12h ? 8 : 8;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      if (input === '') {
        setDisplayValue('');
        setHasError(false);
        onChange(null);
        return;
      }

      if (!inputPattern.test(input)) return;
      if (input.length > maxLength) return;

      setDisplayValue(input);

      const p = parseDisplay(input);
      if (p) {
        setHasError(false);
        onChange(toStorageFormat(p.hours, p.minutes));
      } else if (input.length >= maxLength - 1) {
        setHasError(true);
      } else {
        setHasError(false);
      }
    },
    [inputPattern, maxLength, parseDisplay, onChange],
  );

  const handleBlur = useCallback(() => {
    if (!displayValue.trim()) {
      onChange(null);
      setHasError(false);
      return;
    }

    // Try flexible parsing on blur to accept shorthand formats
    const p = parseFlexible(displayValue);
    if (p) {
      const storage = toStorageFormat(p.hours, p.minutes);
      setDisplayValue(formatForDisplay(storage));
      setHasError(false);
      onChange(storage);
    } else {
      setHasError(true);
    }
  }, [displayValue, formatForDisplay, onChange]);

  // Commit the full time when both hour and minute are selected
  const commitTime = useCallback(
    (h: number, m: number, period: 'AM' | 'PM') => {
      let hour24 = h;
      if (is12h) {
        // h is 1-12 display hour; convert to 24h
        if (period === 'AM') {
          hour24 = h === 12 ? 0 : h;
        } else {
          hour24 = h === 12 ? 12 : h + 12;
        }
      }
      const storage = toStorageFormat(hour24, m);
      setDisplayValue(formatForDisplay(storage));
      setHasError(false);
      onChange(storage);
      setPickerOpen(false);
    },
    [is12h, formatForDisplay, onChange],
  );

  const handleHourSelect = useCallback(
    (h: number) => {
      setPickerHour(h);
    },
    [],
  );

  const handleMinuteSelect = useCallback(
    (m: number) => {
      setPickerMinute(m);
      if (pickerHour !== null) {
        commitTime(pickerHour, m, pickerPeriod);
      }
    },
    [pickerHour, pickerPeriod, commitTime],
  );

  const handlePeriodChangeFromPicker = useCallback(
    (p: 'AM' | 'PM') => {
      setPickerPeriod(p);
      if (pickerHour !== null && pickerMinute !== null) {
        commitTime(pickerHour, pickerMinute, p);
      }
    },
    [pickerHour, pickerMinute, commitTime],
  );

  const isError = hasError || error;
  const defaultPlaceholder = is12h ? 'HH:MM AM' : 'HH:MM';

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <Label htmlFor={inputId} className="mb-1.5 block">
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </Label>
      )}
      <Popover open={pickerOpen} onOpenChange={handlePickerOpenChange}>
        <div className="relative">
          <Input
            id={inputId}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder || defaultPlaceholder}
            disabled={disabled}
            className={cn(
              'pe-10',
              size === 'sm' && 'h-8 text-sm',
              isError && 'border-destructive focus-visible:ring-destructive',
            )}
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute end-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
              disabled={disabled}
              aria-label={t('timeInput.openTimePicker')}
              tabIndex={-1}
            >
              <Clock className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </div>
        <PopoverContent className="w-auto p-2" align="end">
          <ClockFacePicker
            selectedHour={pickerHour}
            selectedMinute={pickerMinute}
            period={pickerPeriod}
            is12h={is12h}
            step={step}
            onHourSelect={handleHourSelect}
            onMinuteSelect={handleMinuteSelect}
            onPeriodChange={handlePeriodChangeFromPicker}
          />
        </PopoverContent>
      </Popover>
      {helperText && (
        <p
          className={cn(
            'text-xs mt-1',
            isError ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
}
